/**
 * Утилиты для сохранения и восстановления истории сообщений в localStorage
 *
 * История сохраняется по sessionId и автоматически очищается при истечении TTL (24 часа)
 */

import { getOrCreateSessionId } from './sessionManager';

const MESSAGE_HISTORY_PREFIX = 'conversation_messages_';
const MAX_MESSAGES = 50; // Хранить максимум 50 последних сообщений

/**
 * Получить ключ для хранения истории сообщений в localStorage
 * @param {string} sessionId - ID сессии
 * @returns {string} Ключ для localStorage
 */
const getStorageKey = (sessionId) => {
  return `${MESSAGE_HISTORY_PREFIX}${sessionId}`;
};

/**
 * Проверить валидность sessionId
 * @param {string} sessionId - ID сессии для проверки
 * @returns {boolean} true если sessionId валиден (совпадает с текущим)
 */
const isSessionIdValid = (sessionId) => {
  const currentSessionId = getOrCreateSessionId();
  return sessionId === currentSessionId;
};

/**
 * Сохранить историю сообщений в localStorage
 * Автоматически ограничивает количество сообщений до MAX_MESSAGES (последние 50)
 *
 * @param {string} sessionId - ID сессии
 * @param {Array} messages - Массив сообщений [{role, text, timestamp}]
 * @returns {boolean} true если сохранение успешно
 */
export const saveMessages = (sessionId, messages) => {
  const startTime = performance.now();
  try {
    if (!sessionId || !Array.isArray(messages)) {
      console.error('[MessageHistory] Invalid parameters for saveMessages');
      return false;
    }

    // Ограничиваем количество сообщений (последние 50)
    const limitedMessages = messages.slice(-MAX_MESSAGES);

    const key = getStorageKey(sessionId);
    const data = {
      sessionId,
      messages: limitedMessages,
      savedAt: Date.now()
    };

    localStorage.setItem(key, JSON.stringify(data));

    const duration = (performance.now() - startTime).toFixed(2);
    console.log(`[MessageHistory] ⏱️ saveMessages took ${duration}ms - Saved ${limitedMessages.length} messages for session ${sessionId.substring(0, 8)}...`);
    return true;
  } catch (error) {
    const duration = (performance.now() - startTime).toFixed(2);
    console.error(`[MessageHistory] ⏱️ saveMessages failed after ${duration}ms:`, error);
    return false;
  }
};

/**
 * Загрузить историю сообщений из localStorage
 * Автоматически проверяет валидность sessionId и очищает устаревшие данные
 *
 * @param {string} sessionId - ID сессии
 * @returns {Array} Массив сообщений или пустой массив если история не найдена/невалидна
 */
export const loadMessages = (sessionId) => {
  const startTime = performance.now();
  try {
    if (!sessionId) {
      console.error('[MessageHistory] Invalid sessionId for loadMessages');
      return [];
    }

    // Проверяем валидность sessionId
    const validationStart = performance.now();
    if (!isSessionIdValid(sessionId)) {
      const validationDuration = (performance.now() - validationStart).toFixed(2);
      console.log(`[MessageHistory] ⏱️ Session validation took ${validationDuration}ms - SessionId ${sessionId.substring(0, 8)}... is no longer valid, clearing history`);
      // eslint-disable-next-line no-use-before-define -- pre-existing: the reference is inside a deferred callback, so it resolves after the declaration (#1206 enabled this rule)
      clearMessages(sessionId);
      return [];
    }
    const validationDuration = (performance.now() - validationStart).toFixed(2);

    const key = getStorageKey(sessionId);
    const localStorageReadStart = performance.now();
    const stored = localStorage.getItem(key);
    const localStorageReadDuration = (performance.now() - localStorageReadStart).toFixed(2);

    if (!stored) {
      const totalDuration = (performance.now() - startTime).toFixed(2);
      console.log(`[MessageHistory] ⏱️ loadMessages took ${totalDuration}ms (localStorage read: ${localStorageReadDuration}ms) - No history found for session ${sessionId.substring(0, 8)}...`);
      return [];
    }

    const parseStart = performance.now();
    const data = JSON.parse(stored);
    const parseDuration = (performance.now() - parseStart).toFixed(2);

    // Валидация структуры данных
    if (!data.messages || !Array.isArray(data.messages)) {
      console.warn('[MessageHistory] Invalid data structure, clearing history');
      // eslint-disable-next-line no-use-before-define -- pre-existing: the reference is inside a deferred callback, so it resolves after the declaration (#1206 enabled this rule)
      clearMessages(sessionId);
      return [];
    }

    const totalDuration = (performance.now() - startTime).toFixed(2);
    console.log(`[MessageHistory] ⏱️ loadMessages took ${totalDuration}ms (validation: ${validationDuration}ms, localStorage read: ${localStorageReadDuration}ms, JSON parse: ${parseDuration}ms) - Loaded ${data.messages.length} messages for session ${sessionId.substring(0, 8)}...`);
    return data.messages;
  } catch (error) {
    const totalDuration = (performance.now() - startTime).toFixed(2);
    console.error(`[MessageHistory] ⏱️ loadMessages failed after ${totalDuration}ms:`, error);
    return [];
  }
};

/**
 * Очистить историю сообщений для конкретного sessionId
 *
 * @param {string} sessionId - ID сессии
 * @returns {boolean} true если очистка успешна
 */
export const clearMessages = (sessionId) => {
  try {
    if (!sessionId) {
      console.error('[MessageHistory] Invalid sessionId for clearMessages');
      return false;
    }

    const key = getStorageKey(sessionId);
    localStorage.removeItem(key);

    console.log(`[MessageHistory] Cleared history for session ${sessionId.substring(0, 8)}...`);
    return true;
  } catch (error) {
    console.error('[MessageHistory] Failed to clear messages:', error);
    return false;
  }
};

/**
 * Очистить всю устаревшую историю сообщений из localStorage
 * Полезно вызывать при старте приложения для очистки старых данных
 *
 * @returns {number} Количество очищенных записей
 */
export const cleanupExpiredHistory = () => {
  const startTime = performance.now();
  try {
    const currentSessionId = getOrCreateSessionId();
    let cleanedCount = 0;
    const totalKeys = localStorage.length;

    // Проходим по всем ключам в localStorage
    const iterationStart = performance.now();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      // Проверяем ключи истории сообщений
      if (key && key.startsWith(MESSAGE_HISTORY_PREFIX)) {
        const storedSessionId = key.replace(MESSAGE_HISTORY_PREFIX, '');

        // Если это не текущая сессия - удаляем
        if (storedSessionId !== currentSessionId) {
          localStorage.removeItem(key);
          cleanedCount++;
        }
      }
    }
    const iterationDuration = (performance.now() - iterationStart).toFixed(2);
    const totalDuration = (performance.now() - startTime).toFixed(2);

    if (cleanedCount > 0) {
      console.log(`[MessageHistory] ⏱️ cleanupExpiredHistory took ${totalDuration}ms (iteration: ${iterationDuration}ms, checked ${totalKeys} keys) - Cleaned up ${cleanedCount} expired entries`);
    } else {
      console.log(`[MessageHistory] ⏱️ cleanupExpiredHistory took ${totalDuration}ms (checked ${totalKeys} keys) - No expired entries found`);
    }

    return cleanedCount;
  } catch (error) {
    const totalDuration = (performance.now() - startTime).toFixed(2);
    console.error(`[MessageHistory] ⏱️ cleanupExpiredHistory failed after ${totalDuration}ms:`, error);
    return 0;
  }
};
